import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import PostAddIcon from "@material-ui/icons/PostAdd";
import PublishIcon from "@material-ui/icons/Publish";
import ArchiveIcon from "@material-ui/icons/Archive";
import DeleteIcon from "@material-ui/icons/Delete";
import { marked } from "marked";

import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

// The three documents the consumer onboarding gates know about. Admins may still
// create a new doc_key by typing it, but these cover the standard set.
const KNOWN_DOC_KEYS = [
  { key: "TERMS", label: "Terms and Conditions" },
  { key: "PRIVACY_NOTICE", label: "Privacy Notice" },
  { key: "CONSUMER_HEALTH_DATA", label: "Consumer Health Data Privacy Policy" },
];

const EMPTY_FORM = {
  id: null,
  docKey: "TERMS",
  title: "",
  contentMd: "",
  effectiveDate: "",
  required: true,
  displayOrder: 1,
};

const statusColor = (status) => {
  if (status === "LIVE") return "primary";
  if (status === "DRAFT") return "default";
  return "secondary"; // RETIRED
};

const CONFIRM_COPY = {
  publish: {
    title: "Publish this version?",
    body: "Consumers will be re-prompted to accept it, and the previous live version will be retired.",
    confirmLabel: "Publish",
  },
  retire: {
    title: "Retire this version?",
    body: "It will no longer be shown to consumers.",
    confirmLabel: "Retire",
  },
  delete: {
    title: "Delete this draft?",
    body: "This cannot be undone.",
    confirmLabel: "Delete",
  },
};

const toDateInput = (iso) => (iso ? String(iso).substring(0, 10) : "");
const fmtDateTime = (iso) => (iso ? String(iso).replace("T", " ").substring(0, 16) : "—");

export default function ConsentDocuments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "" });

  const [dialogMode, setDialogMode] = useState(null); // "create" | "edit" | "view"
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // true = "Add Consent Document" (free-text new doc_key); false = "New Version" of an existing doc_key
  const [newDocMode, setNewDocMode] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, id: null, verb: null });

  // doc_keys that already exist (used to stop "Add" from duplicating one)
  const existingDocKeys = Array.from(new Set(rows.map((r) => r.docKey)));

  const notify = (message) => setSnack({ open: true, message });

  const parseError = async (res) => {
    try {
      const body = await res.json();
      return body.title || body.detail || body.message || `Request failed (${res.status})`;
    } catch (e) {
      return `Request failed (${res.status})`;
    }
  };

  const loadList = useCallback(() => {
    setLoading(true);
    fetch(apiPath.consentDocuments, { method: "GET", headers: generalAuthTokenHeader() })
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseError(res));
        return res.json();
      })
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => notify(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Brand-new document type: free-text doc_key, starts at version 1.
  const openAddDocument = () => {
    setForm({ ...EMPTY_FORM, docKey: "" });
    setNewDocMode(true);
    setDialogMode("create");
  };

  // New version of an EXISTING document, prefilled from the chosen version's full
  // content so the admin can amend the current markdown. Saved as the next draft
  // version (POST); the doc_key is locked to the source document.
  const openNewVersionFromRow = (id) => {
    fetch(`${apiPath.consentDocuments}/${id}`, { method: "GET", headers: generalAuthTokenHeader() })
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseError(res));
        return res.json();
      })
      .then((doc) => {
        setForm({
          id: null, // new version — not editing the source row
          docKey: doc.docKey,
          title: doc.title || "",
          contentMd: doc.contentMd || "",
          effectiveDate: toDateInput(doc.effectiveDate),
          required: !!doc.required,
          displayOrder: doc.displayOrder == null ? 1 : doc.displayOrder,
        });
        setNewDocMode(false);
        setDialogMode("create");
      })
      .catch((e) => notify(e.message));
  };

  const openEditOrView = (id, mode) => {
    fetch(`${apiPath.consentDocuments}/${id}`, { method: "GET", headers: generalAuthTokenHeader() })
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseError(res));
        return res.json();
      })
      .then((doc) => {
        setForm({
          id: doc.id,
          docKey: doc.docKey,
          title: doc.title || "",
          contentMd: doc.contentMd || "",
          effectiveDate: toDateInput(doc.effectiveDate),
          required: !!doc.required,
          displayOrder: doc.displayOrder == null ? 1 : doc.displayOrder,
        });
        setNewDocMode(false); // edit/view are never the "new doc_key" flow
        setDialogMode(mode);
      })
      .catch((e) => notify(e.message));
  };

  const closeDialog = () => {
    setDialogMode(null);
    setForm(EMPTY_FORM);
  };

  const handleField = (field) => (e) => {
    const value = field === "required" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const normalizedDocKey = () =>
    newDocMode ? form.docKey.trim().toUpperCase().replace(/\s+/g, "_") : form.docKey;

  const buildPayload = () => ({
    docKey: normalizedDocKey(),
    title: form.title,
    contentMd: form.contentMd,
    // send a UTC instant for the chosen calendar date
    effectiveDate: form.effectiveDate ? `${form.effectiveDate}T00:00:00Z` : null,
    required: form.required,
    displayOrder: form.displayOrder === "" ? null : Number(form.displayOrder),
  });

  const saveDraft = () => {
    if (!form.docKey || !form.title) {
      notify("Document type and title are required.");
      return;
    }
    if (newDocMode && existingDocKeys.includes(normalizedDocKey())) {
      notify(`"${normalizedDocKey()}" already exists — use "New Version" instead.`);
      return;
    }
    setSaving(true);
    const isEdit = dialogMode === "edit";
    const url = isEdit ? `${apiPath.consentDocuments}/${form.id}` : apiPath.consentDocuments;
    fetch(url, {
      method: isEdit ? "PUT" : "POST",
      headers: generalAuthTokenHeader(),
      body: JSON.stringify(buildPayload()),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseError(res));
        return res.json();
      })
      .then(() => {
        notify(isEdit ? "Draft updated." : "Draft created.");
        closeDialog();
        loadList();
      })
      .catch((e) => notify(e.message))
      .finally(() => setSaving(false));
  };

  // Open the themed confirmation dialog for a destructive/state-changing action.
  const requestAction = (id, verb) => setConfirm({ open: true, id, verb });

  const runAction = () => {
    const { id, verb } = confirm;
    setConfirm({ open: false, id: null, verb: null });
    const isDelete = verb === "delete";
    const url = isDelete ? `${apiPath.consentDocuments}/${id}` : `${apiPath.consentDocuments}/${id}/${verb}`;
    fetch(url, { method: isDelete ? "DELETE" : "POST", headers: generalAuthTokenHeader() })
      .then(async (res) => {
        if (!res.ok) throw new Error(await parseError(res));
      })
      .then(() => {
        notify(`Document ${verb === "publish" ? "published" : verb === "retire" ? "retired" : "deleted"}.`);
        loadList();
      })
      .catch((e) => notify(e.message));
  };

  const renderActions = (row) => (
    <Box display="flex" alignItems="center">
      <Tooltip title="View">
        <IconButton size="small" onClick={() => openEditOrView(row.id, "view")}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.status === "LIVE" && (
        <Tooltip title="New version (amend this document)">
          <IconButton size="small" color="primary" onClick={() => openNewVersionFromRow(row.id)}>
            <PostAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {row.editable && (
        <Tooltip title="Edit draft">
          <IconButton size="small" onClick={() => openEditOrView(row.id, "edit")}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {row.status === "DRAFT" && (
        <Tooltip title="Publish">
          <IconButton size="small" color="primary" onClick={() => requestAction(row.id, "publish")}>
            <PublishIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {row.status === "LIVE" && (
        <Tooltip title="Retire">
          <IconButton size="small" onClick={() => requestAction(row.id, "retire")}>
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {row.editable && (
        <Tooltip title="Delete draft">
          <IconButton size="small" onClick={() => requestAction(row.id, "delete")}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const isView = dialogMode === "view";

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">MaxAlly Consent Documents</Typography>
        {/* Hidden for now (per request) — re-enable to allow creating brand-new consent document types.
        <Button variant="contained" color="primary" onClick={openAddDocument}>
          Add Consent Document
        </Button>
        */}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Effective</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No consent documents yet.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {row.title || row.docKey}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {row.docKey}
                    </Typography>
                  </TableCell>
                  <TableCell>v{row.version}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.status} color={statusColor(row.status)} />
                  </TableCell>
                  <TableCell>{toDateInput(row.effectiveDate) || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {fmtDateTime(row.updatedDate || row.createdDate)}
                      {row.updatedBy || row.createdBy ? ` · ${row.updatedBy || row.createdBy}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{renderActions(row)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit / View dialog */}
      <Dialog open={!!dialogMode} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === "create" && (newDocMode ? "Add Consent Document" : `New Version — ${form.docKey}`)}
          {dialogMode === "edit" && "Edit Draft"}
          {dialogMode === "view" && `${form.title || form.docKey}`}
        </DialogTitle>
        <DialogContent dividers>
          {!isView && (
            <Box display="flex" style={{ gap: 16 }} mb={2} flexWrap="wrap">
              {newDocMode ? (
                <TextField
                  label="Document Type (key)"
                  value={form.docKey}
                  onChange={handleField("docKey")}
                  placeholder="e.g. CALIFORNIA_PRIVACY"
                  helperText="New document key — UPPERCASE, no spaces"
                  style={{ minWidth: 260 }}
                />
              ) : (
                <TextField
                  label="Document Type"
                  value={(() => {
                    const known = KNOWN_DOC_KEYS.find((d) => d.key === form.docKey);
                    return known ? `${known.label} (${form.docKey})` : form.docKey;
                  })()}
                  disabled
                  style={{ minWidth: 260 }}
                />
              )}
              <TextField
                label="Title"
                value={form.title}
                onChange={handleField("title")}
                // A new version of an existing document keeps the document's title.
                disabled={dialogMode === "create" && !newDocMode}
                helperText={dialogMode === "create" && !newDocMode ? "Title carries over from the document" : undefined}
                style={{ minWidth: 280, flex: 1 }}
              />
              <TextField
                label="Effective Date"
                type="date"
                value={form.effectiveDate}
                onChange={handleField("effectiveDate")}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Display Order"
                type="number"
                value={form.displayOrder}
                onChange={handleField("displayOrder")}
                style={{ width: 130 }}
              />
            </Box>
          )}

          {isView ? (
            <Box
              p={2}
              style={{ border: "1px solid #eee", borderRadius: 4, maxHeight: "65vh", overflow: "auto" }}
              dangerouslySetInnerHTML={{ __html: marked.parse(form.contentMd || "") }}
            />
          ) : (
            <Box display="flex" style={{ gap: 16 }}>
              <Box flex={1} display="flex" flexDirection="column">
                <Typography variant="caption" color="textSecondary">
                  Markdown
                </Typography>
                <textarea
                  value={form.contentMd}
                  onChange={handleField("contentMd")}
                  style={{
                    width: "100%",
                    minHeight: "55vh",
                    fontFamily: "monospace",
                    fontSize: 13,
                    padding: 8,
                    boxSizing: "border-box",
                  }}
                />
              </Box>
              <Box flex={1} display="flex" flexDirection="column">
                <Typography variant="caption" color="textSecondary">
                  Preview
                </Typography>
                <Box
                  p={1}
                  style={{ border: "1px solid #eee", borderRadius: 4, minHeight: "55vh", overflow: "auto" }}
                  dangerouslySetInnerHTML={{ __html: marked.parse(form.contentMd || "") }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
          {!isView && (
            <Button onClick={saveDraft} color="primary" variant="contained" disabled={saving}>
              {dialogMode === "create" ? "Create Draft" : "Save Draft"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Themed confirmation dialog for publish / retire / delete */}
      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, id: null, verb: null })} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm.verb ? CONFIRM_COPY[confirm.verb].title : ""}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirm.verb ? CONFIRM_COPY[confirm.verb].body : ""}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, id: null, verb: null })}>Cancel</Button>
          <Button
            onClick={runAction}
            color={confirm.verb === "delete" ? "secondary" : "primary"}
            variant="contained"
          >
            {confirm.verb ? CONFIRM_COPY[confirm.verb].confirmLabel : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack({ open: false, message: "" })}
        message={snack.message}
      />
    </Box>
  );
}
